import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
  GroupByLookupsDocument,
  TeamsTeam,
} from 'generated/resources/graphql'
import { capitalize, compact } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import { countryName } from 'modules/core/lib/countryList'

import { GroupByType } from 'modules/performance_analytics/components/helpers'

const MAX_IDS_LIMIT = 300

export const useGroupByKeysLookupTransformer = (
  groupBy: GroupByType | undefined,
  ids: number[] | string[]
): {
  buckets: string[]
  loading: boolean
  legalEntities?: AnalyticsPageLegalEntitiesFragment[]
  teams?: Pick<TeamsTeam, 'id' | 'name'>[]
  locations?: AnalyticsPageLocationsFragment[]
  employees?: AnalyticsPageEmployeeNamesFragment[]
} => {
  const { data, loading } = useQuery(GroupByLookupsDocument, {
    variables: {
      groupByTeam: groupBy === 'team',
      groupByManager: groupBy === 'manager',
      groupByLocation: groupBy === 'workspace',
      groupByLegalEntity: groupBy === 'legal_entity',
      ids: compact(ids.slice(0, MAX_IDS_LIMIT).map((id) => parseInt(id.toString()))),
    },
  })

  let lookupMap: Map<string, string> = new Map()

  if (groupBy === 'team') {
    const transformedData = data?.teams?.teams
    if (transformedData) {
      lookupMap = new Map(
        transformedData.map((item) => [item.id.toString(), capitalize(item.name)])
      )
    }
  }

  if (groupBy === 'manager') {
    const transformedData = data?.employees?.employees
    if (transformedData) {
      lookupMap = new Map(
        transformedData.map((item) => [item.id.toString(), capitalize(item.fullName)])
      )
    }
  }

  if (groupBy === 'workspace') {
    const transformedData = data?.locations?.locations
    if (transformedData) {
      lookupMap = new Map(
        transformedData.map((item) => [item.id.toString(), capitalize(item.name)])
      )
    }
  }

  if (groupBy === 'legal_entity') {
    const transformedData = data?.companies?.legalEntities
    if (transformedData) {
      lookupMap = new Map(
        transformedData.map((item) => [item.id.toString(), capitalize(item.legalName)])
      )
    }
  }

  if (groupBy === 'nationality') {
    return {
      buckets: ids.map((id) => countryName(id.toString()) || id.toString()),
      loading: false,
    }
  }
  if (groupBy === 'gender') {
    return {
      buckets: ids.map((id) => capitalize(id.toString())),
      loading: false,
    }
  }

  // Map the ids to names in the same order, fallback to id.toString() if name not found
  return {
    buckets: ids.map((id) => lookupMap.get(id.toString()) || id.toString()),
    loading,
    legalEntities: data?.companies?.legalEntities,
    teams: data?.teams?.teams,
    locations: data?.locations?.locations,
    employees: data?.employees?.employees,
  }
}
